import { useEffect, useState } from "react";
import { X, Play } from "lucide-react";
import { listGallery } from "@/lib/api";

const LAYOUTS = {
  hero:  { span: "md:col-span-8 md:row-span-2", ratio: "aspect-[16/10]" },
  tall:  { span: "md:col-span-4",               ratio: "aspect-[4/5]"  },
  half:  { span: "md:col-span-6",               ratio: "aspect-[4/3]"  },
  wide:  { span: "md:col-span-12",              ratio: "aspect-[21/9]" },
};

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    listGallery()
      .then((r) => setItems(r))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="md:col-span-4">
            <span className="overline">§ 03 — Selected Work</span>
          </div>
          <div className="md:col-span-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Frames from
              <br />
              <span className="text-[#E5A93B]">above the water.</span>
            </h2>
            <p className="text-sm text-white/50 font-mono tracking-wider md:max-w-xs">
              A rolling archive of shoots around Lake Anna — homes, boats, and
              moments only the air can see.
            </p>
          </div>
        </div>

        {loaded && items.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="border border-dashed border-white/10 bg-[#0D1322]/50 py-20 text-center"
          >
            <span className="overline text-white/40">No items yet</span>
            <p className="text-white/50 text-sm mt-3">
              Gallery items can be added from the admin console.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {items.map((it, i) => {
              const lay = LAYOUTS[it.layout] || LAYOUTS.half;
              const isVideo = it.media_type === "video";
              return (
                <button
                  type="button"
                  key={it.id}
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setActive(it)}
                  className={`${lay.span} group relative overflow-hidden bg-[#0D1322] border border-white/5 hover:border-white/20 transition-all duration-500`}
                >
                  <div className={`${lay.ratio} w-full overflow-hidden`}>
                    {isVideo ? (
                      <VideoThumb url={it.url} />
                    ) : (
                      <img
                        src={it.url}
                        alt={it.caption || "Gallery image"}
                        className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="h-14 w-14 rounded-full bg-[#E5A93B]/90 flex items-center justify-center group-hover:scale-110 transition">
                        <Play size={22} className="text-black ml-1" />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between pointer-events-none">
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/80">
                      {it.caption || "—"}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E5A93B] opacity-0 group-hover:opacity-100 transition-opacity">
                      view →
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {active && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setActive(null)}
            className="absolute top-6 right-6 text-white hover:text-[#E5A93B] transition z-10"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <div
            className="max-h-[88vh] max-w-[92vw] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {active.media_type === "video" ? (
              <VideoPlayer url={active.url} />
            ) : (
              <img
                src={active.url}
                alt={active.caption || ""}
                className="max-h-[88vh] max-w-full object-contain"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const youtubeId = (url) => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

const VideoThumb = ({ url }) => {
  const yt = youtubeId(url);
  if (yt) {
    return (
      <img
        src={`https://img.youtube.com/vi/${yt}/hqdefault.jpg`}
        alt="Video thumbnail"
        className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
      />
    );
  }
  return (
    <video
      src={url}
      muted
      preload="metadata"
      className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
    />
  );
};

const VideoPlayer = ({ url }) => {
  const yt = youtubeId(url);
  if (yt) {
    return (
      <iframe
        title="Video"
        src={`https://www.youtube.com/embed/${yt}?autoplay=1`}
        className="w-[88vw] h-[50vw] max-h-[88vh] max-w-[1400px]"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    );
  }
  return (
    <video
      src={url}
      controls
      autoPlay
      playsInline
      className="max-h-[88vh] max-w-full"
    />
  );
};
